import React, { useState, useEffect, useCallback } from 'react';
import './NesineAccounts.css';

const API_BASE = '/api';

function NesineAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUsername, setEditUsername] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', displayName: '', autoBetEnabled: false, autoBetAmount: 50, autoBetCount: 1, manuelBetAmount: 50, manuelBetCount: 1, proxyId: '' });
  const [formSaving, setFormSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [cardMessages, setCardMessages] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [randomInitial, setRandomInitial] = useState(false);
  const [coInitialCount, setCoInitialCount] = useState(0);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/nesine-accounts`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setAccounts(data.data);
    } catch (err) { console.error('Accounts fetch error:', err); }
    setLoading(false);
  }, [token]);

  const fetchProxies = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/proxies`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setProxies(data.data || []);
    } catch (err) { console.error('Proxies fetch error:', err); }
  }, [token]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/settings`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setRandomInitial(data.data.randomInitial || false);
        setCoInitialCount(data.data.coInitialCount || 0);
      }
    } catch (err) { console.error('Settings fetch error:', err); }
  }, [token]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/nesine/status`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setAccounts(prev => prev.map(acc => {
          const live = data.data.find(d => d.username === acc.username);
          if (!live) return acc;
          return { ...acc, session: { ...acc.session, isLoggedIn: live.isLoggedIn, balance: live.balance, balanceUpdatedAt: live.balanceUpdatedAt, memberModel: live.memberModel, loginTime: live.loginTime } };
        }));
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchAccounts();
    fetchProxies();
    fetchSettings();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchAccounts, fetchProxies, fetchSettings, fetchStatus]);

  const resetForm = () => {
    setForm({ username: '', password: '', displayName: '', autoBetEnabled: false, autoBetAmount: 50, autoBetCount: 1, manuelBetAmount: 50, manuelBetCount: 1, proxyId: '' });
    setEditUsername(null);
    setShowForm(false);
  };

  const openEdit = (acc) => {
    setForm({
      username: acc.username,
      password: acc.password,
      displayName: acc.displayName || '',
      autoBetEnabled: acc.autoBetEnabled,
      autoBetAmount: acc.autoBetAmount,
      autoBetCount: acc.autoBetCount,
      manuelBetAmount: acc.manuelBetAmount,
      manuelBetCount: acc.manuelBetCount,
      proxyId: acc.proxyId?._id || acc.proxyId || '',
    });
    setEditUsername(acc.username);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const url = editUsername
        ? `${API_BASE}/nesine-accounts/${encodeURIComponent(editUsername)}`
        : `${API_BASE}/nesine-accounts`;
      const method = editUsername ? 'PUT' : 'POST';
      const body = { ...form };
      if (!body.proxyId) body.proxyId = null;

      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: editUsername ? 'Hesap güncellendi ✅' : 'Hesap oluşturuldu ✅' });
        resetForm();
        fetchAccounts();
      } else {
        setMessage({ type: 'error', text: data.message || 'Kaydetme başarısız.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Hata: ' + err.message });
    }
    setFormSaving(false);
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`"${username}" hesabını silmek istediğinize emin misiniz?`)) return;
    try {
      const res = await fetch(`${API_BASE}/nesine-accounts/${encodeURIComponent(username)}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Hesap silindi ✅' });
        fetchAccounts();
      } else {
        setMessage({ type: 'error', text: data.message || 'Silme başarısız.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Hata: ' + err.message });
    }
  };

  const accountAction = async (username, action) => {
    setActionLoading(prev => ({ ...prev, [`${username}-${action}`]: true }));
    setCardMessages(prev => ({ ...prev, [username]: null }));
    try {
      let res;
      if (action === 'auto-login') {
        res = await fetch(`${API_BASE}/nesine/auto-login`, { method: 'POST', headers: headers(), body: JSON.stringify({ username }) });
      } else if (action === 'logout') {
        res = await fetch(`${API_BASE}/nesine/logout`, { method: 'POST', headers: headers(), body: JSON.stringify({ username }) });
      } else if (action === 'verify') {
        res = await fetch(`${API_BASE}/nesine/verify?username=${encodeURIComponent(username)}`, { headers: headers() });
      } else if (action === 'balance') {
        res = await fetch(`${API_BASE}/nesine/balance?username=${encodeURIComponent(username)}`, { headers: headers() });
      }
      const data = await res.json();
      if (data.success) {
        setCardMessages(prev => ({ ...prev, [username]: { type: 'success', text: `${data.message || action + ' başarılı'} ✅` } }));
        fetchStatus();
      } else {
        setCardMessages(prev => ({ ...prev, [username]: { type: 'error', text: `${data.message || action + ' başarısız'}` } }));
      }
    } catch (err) {
      setCardMessages(prev => ({ ...prev, [username]: { type: 'error', text: `Hata - ${err.message}` } }));
    }
    setActionLoading(prev => ({ ...prev, [`${username}-${action}`]: false }));
  };

  const toggleField = async (username, field, currentValue) => {
    setActionLoading(prev => ({ ...prev, [`${username}-${field}`]: true }));
    try {
      const res = await fetch(`${API_BASE}/nesine-accounts/${encodeURIComponent(username)}`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ [field]: !currentValue }),
      });
      const data = await res.json();
      if (data.success) {
        if (field === 'isInitialAccount' && !currentValue) {
          // Diğer hesaplardan initial kaldır
          setAccounts(prev => prev.map(a => ({ ...a, isInitialAccount: a.username === username })));
        } else {
          setAccounts(prev => prev.map(a => a.username === username ? { ...a, [field]: !currentValue } : a));
        }
      }
    } catch (err) { console.error('Toggle error:', err); }
    setActionLoading(prev => ({ ...prev, [`${username}-${field}`]: false }));
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' }) : '-';

  const toggleRandomInitial = async () => {
    setSettingsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/settings/randomInitial`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ enabled: !randomInitial }),
      });
      const data = await res.json();
      if (data.success) setRandomInitial(data.data.randomInitial);
    } catch (err) { console.error('Toggle randomInitial error:', err); }
    setSettingsLoading(false);
  };

  const updateCoInitialCount = async (newValue) => {
    const num = parseInt(newValue, 10);
    if (isNaN(num) || num < 0) return;
    setCoInitialCount(num);
    try {
      const res = await fetch(`${API_BASE}/settings/coInitialCount`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ value: num }),
      });
      const data = await res.json();
      if (data.success) setCoInitialCount(data.data.coInitialCount);
    } catch (err) { console.error('Update coInitialCount error:', err); }
  };

  return (
    <div className="na-page">
      {/* Page Header */}
      <div className="na-page-header">
        <div className="na-page-header-top">
          <div>
            <h1>Nesine Hesapları</h1>
            <p className="na-page-subtitle">{accounts.length} hesap kayıtlı</p>
          </div>
          <div className="na-header-actions">
            <label className="na-header-toggle na-initial-count-group">
              <span>🔗 Co-Initial</span>
              <input
                type="number"
                min="0"
                className="na-initial-count-input"
                value={coInitialCount}
                onChange={(e) => updateCoInitialCount(e.target.value)}
              />
            </label>
            <label className="na-header-toggle">
              <span>🎲 Random Initial</span>
              <label className="switch">
                <input type="checkbox" checked={randomInitial} onChange={toggleRandomInitial} disabled={settingsLoading} />
                <span className="slider"></span>
              </label>
            </label>
            <button className="na-add-btn" onClick={() => { resetForm(); setShowForm(true); }}>
              + Yeni Hesap Ekle
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`na-alert na-alert-${message.type}`}>
          <span>{message.text}</span>
          <button className="na-alert-close" onClick={() => setMessage({ type: '', text: '' })}>×</button>
        </div>
      )}

      {/* Add/Edit Form — only for NEW accounts */}
      {showForm && !editUsername && (
        <div className="na-form-card">
          <div className="na-form-header">
            <h2>{editUsername ? `✏️ ${editUsername} Düzenle` : '➕ Yeni Hesap Oluştur'}</h2>
            <p>Nesine hesap bilgilerini girin</p>
          </div>
          <form onSubmit={handleSave}>
            <div className="na-form-section">
              <h3>Hesap Bilgileri</h3>
              <div className="na-form-row">
                <div className="na-field">
                  <label>Kullanıcı Adı</label>
                  <input type="text" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} required disabled={!!editUsername} placeholder="TC No girin" />
                </div>
                <div className="na-field">
                  <label>Şifre</label>
                  <input type="text" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required placeholder="Nesine şifresi" />
                </div>
              </div>
              <div className="na-form-row">
                <div className="na-field">
                  <label>Görünen Ad</label>
                  <input type="text" value={form.displayName} onChange={e => setForm(p => ({ ...p, displayName: e.target.value }))} placeholder="ör: Hesap 1" />
                </div>
                <div className="na-field">
                  <label>Proxy</label>
                  <select value={form.proxyId} onChange={e => setForm(p => ({ ...p, proxyId: e.target.value }))}>
                    <option value="">Proxy yok</option>
                    {proxies.map(p => (
                      <option key={p._id} value={p._id}>{p.name || p.proxyUrl || p._id}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="na-form-section">
              <h3>Ayarlar</h3>
              <div className="na-form-switches">
                <label className="na-switch-row">
                  <div className="na-switch-info">
                    <span className="na-switch-title">Otomatik Kupon</span>
                    <span className="na-switch-desc">Gol geldiğinde otomatik bahis yap</span>
                  </div>
                  <label className="switch"><input type="checkbox" checked={form.autoBetEnabled} onChange={e => setForm(p => ({ ...p, autoBetEnabled: e.target.checked }))} /><span className="slider"></span></label>
                </label>
              </div>
            </div>

            <div className="na-form-section">
              <h3>Bahis Tutarları</h3>
              <div className="na-form-row na-form-row-4">
                <div className="na-field">
                  <label>Oto Tutar (₺)</label>
                  <input type="number" min="1" value={form.autoBetAmount} onChange={e => setForm(p => ({ ...p, autoBetAmount: Number(e.target.value) }))} />
                </div>
                <div className="na-field">
                  <label>Oto Adet</label>
                  <input type="number" min="1" value={form.autoBetCount} onChange={e => setForm(p => ({ ...p, autoBetCount: Number(e.target.value) }))} />
                </div>
                <div className="na-field">
                  <label>Manuel Tutar (₺)</label>
                  <input type="number" min="1" value={form.manuelBetAmount} onChange={e => setForm(p => ({ ...p, manuelBetAmount: Number(e.target.value) }))} />
                </div>
                <div className="na-field">
                  <label>Manuel Adet</label>
                  <input type="number" min="1" value={form.manuelBetCount} onChange={e => setForm(p => ({ ...p, manuelBetCount: Number(e.target.value) }))} />
                </div>
              </div>
            </div>

            <div className="na-form-footer">
              <button type="button" className="na-btn-secondary" onClick={() => { resetForm(); setShowForm(false); }}>\u0130ptal</button>
              <button type="submit" className="na-btn-primary" disabled={formSaving}>
                {formSaving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Accounts */}
      {loading ? (
        <div className="na-loading"><div className="spinner" /><p>Hesaplar yükleniyor...</p></div>
      ) : accounts.length === 0 ? (
        <div className="na-empty">
          <span className="na-empty-icon">📭</span>
          <h3>Henüz hesap eklenmemiş</h3>
          <p>Başlamak için yukarıdan yeni bir hesap ekleyin</p>
        </div>
      ) : (
        <div className="na-cards">
          {accounts.map(acc => {
            const isLoggedIn = acc.session?.isLoggedIn;
            const balance = acc.session?.balance;
            const proxyName = acc.proxyId?.name || acc.proxyId?.proxyUrl || null;

            return (
                <div key={acc.username} className={`na-card ${isLoggedIn ? 'na-card-online' : 'na-card-offline'} ${acc.isInitialAccount ? 'na-card-initial' : ''}`}>
                {/* Card Top - Status & Name */}
                <div className="na-card-top">
                  <div className="na-card-identity">
                    <div className={`na-avatar ${isLoggedIn ? 'na-avatar-on' : 'na-avatar-off'}`}>
                      {(acc.displayName || acc.username).charAt(0).toUpperCase()}
                    </div>
                    <div className="na-card-name-block">
                      <h3>{acc.displayName || acc.username}</h3>
                      <span className="na-card-username">{acc.username}</span>
                    </div>
                  </div>
                  <div className="na-card-status-badges">
                    <span className={`na-status-pill ${isLoggedIn ? 'na-pill-online' : 'na-pill-offline'}`}>
                      <span className="na-pill-dot" /> {isLoggedIn ? 'Çevrimiçi' : 'Çevrimdışı'}
                    </span>
                    {isLoggedIn && (
                      <>
                        <button className="na-btn na-btn-green na-btn-sm" onClick={() => accountAction(acc.username, 'verify')} disabled={actionLoading[`${acc.username}-verify`]}>
                          {actionLoading[`${acc.username}-verify`] ? '⏳' : '✅'} Kontrol
                        </button>
                        <button className="na-btn na-btn-teal na-btn-sm" onClick={() => accountAction(acc.username, 'balance')} disabled={actionLoading[`${acc.username}-balance`]}>
                          {actionLoading[`${acc.username}-balance`] ? '⏳' : '💰'} Bakiye
                        </button>
                      </>
                    )}
                    <label className="na-toggle-item na-toggle-compact">
                      <span>Oto Kupon</span>
                      <label className="switch"><input type="checkbox" checked={acc.autoBetEnabled} onChange={() => toggleField(acc.username, 'autoBetEnabled', acc.autoBetEnabled)} disabled={actionLoading[`${acc.username}-autoBetEnabled`]} /><span className="slider"></span></label>
                    </label>
                    <label className="na-toggle-item na-toggle-compact">
                      <span>Initial</span>
                      <label className="switch"><input type="checkbox" checked={acc.isInitialAccount} onChange={() => toggleField(acc.username, 'isInitialAccount', acc.isInitialAccount)} disabled={actionLoading[`${acc.username}-isInitialAccount`]} /><span className="slider"></span></label>
                    </label>
                    <div className="na-top-actions">
                      {isLoggedIn ? (
                        <button className="na-btn na-btn-orange na-btn-sm" onClick={() => accountAction(acc.username, 'logout')} disabled={actionLoading[`${acc.username}-logout`]}>
                          {actionLoading[`${acc.username}-logout`] ? '⏳' : '🔓'} Çıkış
                        </button>
                      ) : (
                        <button className="na-btn na-btn-login-sm" onClick={() => accountAction(acc.username, 'auto-login')} disabled={actionLoading[`${acc.username}-auto-login`]}>
                          {actionLoading[`${acc.username}-auto-login`] ? '⏳' : '🔐'} Giriş Yap
                        </button>
                      )}
                      <button className="na-btn na-btn-blue na-btn-sm" onClick={() => openEdit(acc)}>✏️ Düzenle</button>
                      <button className="na-btn na-btn-red na-btn-sm" onClick={() => handleDelete(acc.username)}>🗑️ Sil</button>
                    </div>
                  </div>
                </div>

                {/* Inline Edit Form */}
                {editUsername === acc.username && (
                  <div className="na-inline-edit">
                    <form onSubmit={handleSave}>
                      <div className="na-inline-edit-grid">
                        <div className="na-field">
                          <label>Şifre</label>
                          <input type="text" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required placeholder="Nesine şifresi" />
                        </div>
                        <div className="na-field">
                          <label>Görünen Ad</label>
                          <input type="text" value={form.displayName} onChange={e => setForm(p => ({ ...p, displayName: e.target.value }))} placeholder="ör: Hesap 1" />
                        </div>
                        <div className="na-field">
                          <label>Proxy</label>
                          <select value={form.proxyId} onChange={e => setForm(p => ({ ...p, proxyId: e.target.value }))}>
                            <option value="">Proxy yok</option>
                            {proxies.map(p => (
                              <option key={p._id} value={p._id}>{p.name || p.proxyUrl || p._id}</option>
                            ))}
                          </select>
                        </div>
                        <div className="na-field">
                          <label>Oto Tutar (₺)</label>
                          <input type="number" min="1" value={form.autoBetAmount} onChange={e => setForm(p => ({ ...p, autoBetAmount: Number(e.target.value) }))} />
                        </div>
                        <div className="na-field">
                          <label>Oto Adet</label>
                          <input type="number" min="1" value={form.autoBetCount} onChange={e => setForm(p => ({ ...p, autoBetCount: Number(e.target.value) }))} />
                        </div>
                        <div className="na-field">
                          <label>Manuel Tutar (₺)</label>
                          <input type="number" min="1" value={form.manuelBetAmount} onChange={e => setForm(p => ({ ...p, manuelBetAmount: Number(e.target.value) }))} />
                        </div>
                        <div className="na-field">
                          <label>Manuel Adet</label>
                          <input type="number" min="1" value={form.manuelBetCount} onChange={e => setForm(p => ({ ...p, manuelBetCount: Number(e.target.value) }))} />
                        </div>
                        <div className="na-field na-field-switch">
                          <label>Oto Kupon</label>
                          <label className="switch"><input type="checkbox" checked={form.autoBetEnabled} onChange={e => setForm(p => ({ ...p, autoBetEnabled: e.target.checked }))} /><span className="slider"></span></label>
                        </div>
                      </div>
                      <div className="na-inline-edit-actions">
                        <button type="button" className="na-btn-secondary na-btn-sm" onClick={() => { setEditUsername(null); resetForm(); }}>İptal</button>
                        <button type="submit" className="na-btn-primary na-btn-sm" disabled={formSaving}>
                          {formSaving ? '⏳' : '💾'} Kaydet
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Card Message */}
                {cardMessages[acc.username] && (
                  <div className={`na-card-alert na-card-alert-${cardMessages[acc.username].type}`}>
                    <span>{cardMessages[acc.username].text}</span>
                    <button className="na-alert-close" onClick={() => setCardMessages(prev => ({ ...prev, [acc.username]: null }))}>×</button>
                  </div>
                )}

                {/* Balance Section */}
                <div className="na-card-balance-section">
                  <div className="na-balance-box">
                    <span className="na-balance-label">Bakiye</span>
                    <span className="na-balance-amount">{balance != null ? (() => { const parts = Number(balance).toFixed(2).split('.'); const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'); return <>{intPart}<span className="na-balance-decimal">,{parts[1]}</span> ₺</>; })() : '—'}</span>
                  </div>
                  {isLoggedIn && acc.session?.memberModel && (
                    <div className="na-member-name">
                      {acc.session.memberModel.name} {acc.session.memberModel.surname}
                    </div>
                  )}
                </div>

                {/* Info Grid */}
                <div className="na-card-details">
                  <div className="na-detail-row">
                    <div className="na-detail-item">
                      <span className="na-detail-label">Şifre</span>
                      <span className="na-detail-value na-mono">{acc.password}</span>
                    </div>
                    <div className="na-detail-item">
                      <span className="na-detail-label">Oto Kupon</span>
                      <span className="na-detail-value">{Number(acc.autoBetAmount).toLocaleString('tr-TR')}₺ × {acc.autoBetCount}</span>
                    </div>
                    <div className="na-detail-item">
                      <span className="na-detail-label">Manuel Kupon</span>
                      <span className="na-detail-value">{Number(acc.manuelBetAmount).toLocaleString('tr-TR')}₺ × {acc.manuelBetCount}</span>
                    </div>
                    {isLoggedIn && acc.session?.loginTime && (
                      <div className="na-detail-item">
                        <span className="na-detail-label">Giriş Zamanı</span>
                        <span className="na-detail-value">{formatDate(acc.session.loginTime)}</span>
                      </div>
                    )}
                    <div className="na-detail-item">
                      <span className="na-detail-label">Proxy</span>
                      <span className={`na-detail-value ${proxyName ? '' : 'na-detail-muted'}`}>{proxyName || 'Yok'}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NesineAccounts;
